import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { FiMenu, FiX, FiChevronDown, FiAward } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { SITE_CONFIG, homeContent } from '../data/content';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const links = [
    { name: 'Home', path: '/' },
    { name: 'Back pain', path: '/back-pain' },
    { name: 'Neck pain', path: '/neck-pain' },
    { name: 'Headaches', path: '/headaches-migraines' },
  ];

  const teamLinks = homeContent.team.map(member => ({
    name: member.name.replace('Dr ', ''),
    path: `/team/${member.slug}`
  }));

  const moreLinks = [
    { name: 'Massage', path: '/massage' },
    { name: 'X-rays', path: '/x-rays' },
    { name: 'Dry needling', path: '/myofascial-dry-needling' },
    { name: 'Orthotics', path: '/custom-orthotics' },
  ];

  return (
    <>
      <div className="bg-[#0a1e1e] text-teal-50 py-2 px-4 text-center text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] flex items-center justify-center gap-3">
        <SafeIcon icon={FiAward} className="text-teal-400" />
        <span>{SITE_CONFIG.anniversary}</span>
        <SafeIcon icon={FiAward} className="text-teal-400" />
      </div>
      
      <nav className="bg-white/95 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            <div className="flex items-center">
              <Link to="/" className="flex items-center gap-3">
                <img src={SITE_CONFIG.logo} alt="CBR Health" className="h-10 md:h-12 w-auto" />
              </Link>
            </div>

            <div className="hidden lg:flex items-center space-x-6">
              {links.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`text-xs font-bold uppercase tracking-wider transition-colors ${
                    location.pathname === link.path ? 'text-teal-600' : 'text-gray-600 hover:text-teal-600'
                  }`}
                >
                  {link.name}
                </Link>
              ))}

              <div className="relative group">
                <button className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-gray-600 hover:text-teal-600">
                  Our Team <SafeIcon icon={FiChevronDown} />
                </button>
                <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-gray-100 shadow-xl rounded-2xl py-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  {teamLinks.map(l => (
                    <Link key={l.path} to={l.path} className="block px-6 py-2 text-sm font-bold text-gray-700 hover:bg-teal-50 hover:text-teal-600">
                      {l.name}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="relative group">
                <button className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-gray-600 hover:text-teal-600">
                  More <SafeIcon icon={FiChevronDown} />
                </button>
                <div className="absolute top-full right-0 mt-2 w-48 bg-white border border-gray-100 shadow-xl rounded-2xl py-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  {moreLinks.map(l => (
                    <Link key={l.path} to={l.path} className="block px-6 py-2 text-sm font-bold text-gray-700 hover:bg-teal-50 hover:text-teal-600">
                      {l.name}
                    </Link>
                  ))}
                </div>
              </div>

              <a
                href={SITE_CONFIG.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-teal-600 text-white px-6 py-2.5 rounded-full text-sm font-bold hover:bg-teal-700 transition-all shadow-md"
              >
                BOOK ONLINE
              </a>
            </div>

            <div className="flex items-center lg:hidden">
              <button onClick={() => setIsOpen(!isOpen)} className="text-gray-600 p-2">
                <SafeIcon icon={isOpen ? FiX : FiMenu} className="text-2xl" />
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="lg:hidden bg-white border-t overflow-y-auto max-h-[calc(100vh-80px)]"
            >
              <div className="px-4 py-6 space-y-4">
                {links.map((link) => (
                  <Link
                    key={link.name}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className="block text-lg font-bold text-gray-800"
                  >
                    {link.name}
                  </Link>
                ))}
                
                <div className="pt-2">
                  <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3">Our Team</p>
                  <div className="grid grid-cols-1 gap-3">
                    {teamLinks.map(l => (
                      <Link key={l.path} to={l.path} onClick={() => setIsOpen(false)} className="text-gray-600 font-bold hover:text-teal-600">
                        {l.name}
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="pt-2">
                  <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3">More Services</p>
                  <div className="grid grid-cols-1 gap-3">
                    {moreLinks.map(l => (
                      <Link key={l.path} to={l.path} onClick={() => setIsOpen(false)} className="text-gray-600 font-bold hover:text-teal-600">
                        {l.name}
                      </Link>
                    ))}
                  </div>
                </div>

                <a href={SITE_CONFIG.bookingUrl} className="block w-full bg-teal-600 text-white text-center py-4 rounded-xl font-bold">
                  BOOK APPOINTMENT
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};

export default Navbar;